'use client';

import { useEffect, useRef } from 'react';
import { setUserProperties } from '@/lib/analytics';
import { useSession } from '@/hooks/use-session';
import { TermsAcceptanceDialog } from '@/app/(protected)/components/terms-acceptance-dialog';
import { EmailVerificationDialog } from '@/app/(protected)/components/email-verification-dialog';
import { UserConsentBridge } from '@/app/(protected)/components/user-consent-bridge';

/**
 * Hide the Klaro cookie banner for authenticated users.
 * Logged-in users handle consent via terms acceptance, so the
 * cookie dialog is never needed.
 */
function HideKlaroBanner() {
  const hiddenRef = useRef(false);

  useEffect(() => {
    if (hiddenRef.current) return;
    hiddenRef.current = true;

    // Hide immediately if already rendered
    const hide = () => {
      const el = document.querySelector('.klaro');
      if (el) (el as HTMLElement).style.display = 'none';
    };
    hide();

    // Also observe DOM in case Klaro renders after this effect
    const observer = new MutationObserver(() => hide());
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  return null;
}

/**
 * Global auth-related guards rendered at the root layout level.
 * Ensures terms acceptance dialog and consent bridge work on ALL pages,
 * regardless of route group.
 */
export function GlobalAuthGuard() {
  const { data: session, status } = useSession();
  const isAuthenticated = status === 'authenticated';

  // Set GA4 user_role once session with permissions is available
  useEffect(() => {
    if (session?.user) {
      setUserProperties(session.user);
    }
  }, [session]);

  if (!isAuthenticated) return null;

  return (
    <>
      <HideKlaroBanner />
      <UserConsentBridge />
      <TermsAcceptanceDialog />
      <EmailVerificationDialog />
    </>
  );
}
